import React, { useCallback, useState, useEffect } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface JupiterLayersProps {
  width?: number;
  height?: number;
}

export const JupiterLayers: React.FC<JupiterLayersProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);
  const [pulse, setPulse] = useState(1);

  // Subtle CSS pulse effect for the text below
  useEffect(() => {
    let t = 0;
    const interval = setInterval(() => {
      t += 0.05;
      setPulse(1 + Math.sin(t) * 0.1);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerX = size / 2 - 50; // shift left to leave room for labels
    const centerY = size / 2;

    const layers = [
      { name: "Hydrogen Gas Atmosphere", color: '#C88B3A', outer: 145, inner: 105 },
      { name: "Liquid Hydrogen", color: '#A07030', outer: 105, inner: 70 },
      { name: "Metallic Hydrogen", color: '#705020', outer: 70, inner: 40 },
      { name: "Rocky Core (maybe)", color: '#503010', outer: 40, inner: 0 },
    ];

    // Draw layers
    layers.forEach((layer, index) => {
      ctx.save();
      
      // Rotate the outermost layer slowly for a swirl effect
      if (index === 0) {
        ctx.translate(centerX, centerY);
        ctx.rotate((frameCount * 0.002) % (Math.PI * 2));
        ctx.translate(-centerX, -centerY);
        
        // Add subtle band details to outer layer before clipping
        // We draw the base color, then bands
        ctx.beginPath();
        ctx.arc(centerX, centerY, layer.outer, -Math.PI / 2, Math.PI / 2);
        ctx.fillStyle = layer.color;
        ctx.fill();
        
        ctx.clip(); // Clip bands to the layer
        for(let i=0; i<10; i++) {
          ctx.fillStyle = i % 2 === 0 ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
          ctx.fillRect(centerX - layer.outer, centerY - layer.outer + i*30, layer.outer * 2, 15);
        }
      } else {
        ctx.beginPath();
        ctx.arc(centerX, centerY, layer.outer, -Math.PI / 2, Math.PI / 2);
        ctx.fillStyle = layer.color;
        ctx.fill();
      }
      ctx.restore();
    });

    // Draw lines and labels
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = '12px sans-serif';

    layers.forEach((layer) => {
      const midRadius = layer.inner + (layer.outer - layer.inner) / 2;
      // Angle for the line (fan them out slightly)
      const angle = (midRadius / 145) * Math.PI/3 - Math.PI/6; // From -30 to 30 deg
      
      const startX = centerX + Math.cos(angle) * midRadius;
      const startY = centerY + Math.sin(angle) * midRadius;
      
      const endX = centerX + 160;
      const endY = startY;

      // Draw line
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(startX + 20, startY);
      ctx.lineTo(endX, endY);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Draw little dot
      ctx.beginPath();
      ctx.arc(startX, startY, 3, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fill();

      // Draw text
      ctx.fillStyle = '#E2E8F0';
      ctx.fillText(layer.name, endX + 5, endY);
    });

  }, [size]);

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size - 80, position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
        <canvas ref={canvasRef} />
      </div>
      
      <div style={{ 
        marginTop: '10px', 
        width: '100%', 
        textAlign: 'center', 
        backgroundColor: 'rgba(30, 41, 59, 0.8)',
        padding: '12px',
        borderRadius: '8px',
        opacity: Math.max(0.6, pulse), // Subtle CSS animation pulse
        transition: 'opacity 50ms ease-in-out'
      }}>
        <div style={{ color: '#F87171', fontWeight: 'bold', marginBottom: '4px' }}>
          Core Pressure: ~45 million atm
        </div>
        <div style={{ color: '#FBBF24', fontSize: '14px' }}>
          Core Temperature: ~36,000°C
        </div>
      </div>
    </div>
  );
};
